import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { communityService } from "api/services/community.service";
import { reportsService } from "api/services/reports.service";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import Modal from "components/UI/Modal";
import { REASON_LABEL, STATUS_BADGE, STATUS_LABEL, TARGET_TYPE_LABEL } from "pages/community-group/report.constants";
import { WEB_URL } from "utils";

import "./ReportDetailModal.scss";

import type { CommunityPost, CommunityPostListResponse, CommunityPostResponse, Report, ReportReason, ReportStatus } from "api/types/report.types";

// ─────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────
interface ReportDetailModalProps {
  report: Report | null;
  onClose: () => void;
  onStatusUpdated: () => void;
  onOpenResolve: (report: Report) => void;
}

// ─────────────────────────────────────────────
// 컴포넌트
// ─────────────────────────────────────────────
const ReportDetailModal: React.FC<ReportDetailModalProps> = ({ report, onClose, onStatusUpdated, onOpenResolve }) => {
  const navigate = useNavigate();

  const [targetPost, setTargetPost] = useState<CommunityPost | null>(null);
  const [userPosts, setUserPosts] = useState<CommunityPost[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // report가 바뀔 때마다 대상 데이터 조회
  useEffect(() => {
    if (!report) return;
    setTargetPost(null);
    setUserPosts([]);

    if (report.target_type === "post") {
      setDetailLoading(true);
      communityService
        .getPost(report.target_id)
        .then((res) => setTargetPost((res.data as CommunityPostResponse).data ?? null))
        .catch(() => undefined)
        .finally(() => setDetailLoading(false));
    } else if (report.target_type === "user") {
      setDetailLoading(true);
      communityService
        .getUserPosts(report.target_id, 5)
        .then((res) => setUserPosts((res.data as CommunityPostListResponse).data ?? []))
        .catch(() => undefined)
        .finally(() => setDetailLoading(false));
    }
  }, [report]);

  const handleUpdateStatus = async (status: ReportStatus) => {
    if (!report) return;
    setActionLoading(true);
    try {
      await reportsService.updateStatus(report.id, { status });
      onStatusUpdated();
      onClose();
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Modal
      open={!!report}
      title={`신고 상세 #${report?.id ?? 0}`}
      width="600px"
      onClose={onClose}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button variant="secondary" onClick={onClose}>
            닫기
          </Button>
          {report?.status === "pending" && (
            <Button variant="primary" disabled={actionLoading} onClick={() => handleUpdateStatus("reviewing")}>
              {actionLoading ? "처리 중..." : "검토 시작"}
            </Button>
          )}
          {report?.status === "reviewing" && (
            <>
              <Button variant="danger" disabled={actionLoading} onClick={() => handleUpdateStatus("dismissed")}>
                {actionLoading ? "처리 중..." : "기각"}
              </Button>
              <Button variant="primary" disabled={actionLoading} onClick={() => handleUpdateStatus("resolved")}>
                {actionLoading ? "처리 중..." : "처리 완료"}
              </Button>
            </>
          )}
        </div>
      }
    >
      {report && (
        <div className="report-detail">
          {/* 기본 정보 */}
          <div className="report-detail__info-box">
            <div className="report-detail__info-header">
              <Badge variant={STATUS_BADGE[report.status]}>{STATUS_LABEL[report.status]}</Badge>
            </div>
            <div className="report-detail__info-grid">
              <InfoRow label="유형" value={TARGET_TYPE_LABEL[report.target_type]} />
              <InfoRow label="신고 사유" value={`${REASON_LABEL[report.reason as ReportReason]} ${report.description ? "- " + report.description : ""}`} />

              {/* 신고자 — Django PK 클릭 시 유저 상세 이동 */}
              <div>
                <div className="report-detail__reporter-label">신고자</div>
                <div className="report-detail__reporter-value">
                  <span>{report.reporter?.nickname ?? report.reporter?.username ?? report.reporter_id}</span>
                  {report.reporter?.user_id && (
                    <button className="report-detail__reporter-pk" onClick={() => navigate(`/user-list/${report.reporter?.user_id}`)}>
                      #{report.reporter.user_id}
                    </button>
                  )}
                </div>
              </div>

              <InfoRow
                label="대상 ID"
                value={report.target_id}
                mono
                onClick={() => {
                  if (report.target_type === "user") {
                    window.open(`${WEB_URL}/community/user/${report.target_id}`, "_blank");
                  } else {
                    window.open(`${WEB_URL}/community/${report.target_id}`, "_blank");
                  }
                }}
              />
              <InfoRow label="신고일" value={report.created_at.slice(0, 16).replace("T", " ")} />
              {report.reviewed_by && <InfoRow label="처리자" value={report.reviewed_by} />}
              {report.reviewed_at && <InfoRow label="처리일" value={report.reviewed_at.slice(0, 16).replace("T", " ")} />}
            </div>
          </div>

          {/* 게시글 대상: 원문 내용 */}
          {report.target_type === "post" && (
            <div className="report-detail__section">
              <SectionHeader>신고된 게시글</SectionHeader>
              <div className="report-detail__section-body">{detailLoading ? <LoadingText /> : targetPost ? <PostPreview post={targetPost} /> : <EmptyText>게시글을 불러올 수 없습니다.</EmptyText>}</div>
            </div>
          )}

          {/* 유저 대상: 최근 게시글 5개 */}
          {report.target_type === "user" && (
            <div className="report-detail__section">
              <SectionHeader>신고된 사용자 최근 게시글 (5개까지만 보여집니다)</SectionHeader>
              <div className="report-detail__section-body report-detail__section-body--list">
                {detailLoading ? (
                  <LoadingText />
                ) : userPosts.length > 0 ? (
                  <div className="report-detail__post-list">
                    {userPosts.map((post) => (
                      <PostPreview key={post.id} post={post} compact />
                    ))}
                  </div>
                ) : (
                  <EmptyText>게시글이 없습니다.</EmptyText>
                )}
              </div>
            </div>
          )}

          {/* 일괄 처리 버튼 */}
          <div className="report-detail__bulk-action">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onClose();
                onOpenResolve(report);
              }}
            >
              이 대상({TARGET_TYPE_LABEL[report.target_type]})의 신고 전체 처리
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ─────────────────────────────────────────────
// 내부 헬퍼 컴포넌트
// ─────────────────────────────────────────────
const InfoRow: React.FC<{ label: string; value: string; mono?: boolean; onClick?: () => void }> = ({ label, value, mono, onClick }) => (
  <div>
    <div className="info-row__label">{label}</div>
    <div onClick={onClick} className={["info-row__value", mono ? "info-row__value--mono" : "", onClick ? "info-row__value--clickable" : ""].filter(Boolean).join(" ")}>
      {value}
    </div>
  </div>
);

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="report-section-header">{children}</div>;

const LoadingText: React.FC = () => <div className="report-loading-text">불러오는 중...</div>;

const EmptyText: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="report-empty-text">{children}</div>;

const PostPreview: React.FC<{ post: CommunityPost; compact?: boolean }> = ({ post, compact }) => {
  const mediaUrls = post.media_urls ?? [];
  const hasImage = post.media_type === "image" && mediaUrls.length > 0;

  return (
    <div className={`post-preview${compact ? " post-preview--compact" : ""}`}>
      {/* 작성자 + 날짜 */}
      <div className="post-preview__meta">
        {post.author?.profile_image_url && <img src={post.author.profile_image_url} alt="" className="post-preview__avatar" />}
        <span className="post-preview__author">{post.author?.nickname ?? post.author?.name ?? post.user_id}</span>
        <span className="post-preview__date">{String(post.created_at).slice(0, 10)}</span>
        {post.is_deleted === 1 && <Badge variant="rejected">삭제됨</Badge>}
      </div>

      {/* 본문 */}
      <div className={`post-preview__content${compact ? " post-preview__content--compact" : ""}`}>{post.content}</div>

      {/* 이미지 (상세 모드만) */}
      {!compact && hasImage && (
        <div className="post-preview__images">
          {mediaUrls.slice(0, 3).map((url, i) => (
            <img key={i} src={url} alt="" className="post-preview__image" />
          ))}
          {mediaUrls.length > 3 && <div className="post-preview__image-more">+{mediaUrls.length - 3}</div>}
        </div>
      )}
    </div>
  );
};

export default ReportDetailModal;
